import React from "react"
import type { FiinderApi } from "./fiinder-api-types.ts"
import { clearRecentPages } from "@/lib/recent-pages-storage"

export const FiinderApiContext = React.createContext<FiinderApi | undefined>(
  undefined
)

export function useFiinderApi() {
  const ctx = React.useContext(FiinderApiContext)
  if (!ctx) throw new Error("FiinderApiContext not found")
  return ctx
}

// TODO: Temporary - userId will be obtained from another source in the future
function generateUserIdFromToken(token: string): string {
  if (!token) return 'anonymous';
  const encoded = btoa(token).replace(/[^a-zA-Z0-9]/g, '');
  return encoded.slice(0, 8);
}

const CACHED_USER_ID_KEY = "fiinder_cached_user_id";

function getCachedUserId(): string | null {
  try {
    return localStorage.getItem(CACHED_USER_ID_KEY);
  } catch {
    return null;
  }
}

function setCachedUserId(userId: string): void {
  try {
    localStorage.setItem(CACHED_USER_ID_KEY, userId);
  } catch {
    // Silently fail
  }
}

export const UserIdContext = React.createContext<string | undefined>(undefined);

export function useUserId() {
  const ctx = React.useContext(UserIdContext);
  if (!ctx) throw new Error("UserIdContext not found");
  return ctx;
}

export function UserIdProvider({ children }: { children: React.ReactNode }) {
  const token = typeof window !== 'undefined'
    ? window.FIINDER_COMPANY_TOKEN ?? ''
    : '';
  const userId = generateUserIdFromToken(token);

  React.useEffect(() => {
    const cachedUserId = getCachedUserId();
    // Clear recent pages if there's no cached userId (first execution) or if userId changed
    if (!cachedUserId || cachedUserId !== userId) {
      clearRecentPages();
    }
    setCachedUserId(userId);
  }, [userId]);

  return (
    <UserIdContext.Provider value={userId}>
      {children}
    </UserIdContext.Provider>
  );
}
